import React, { useCallback } from 'react';
import classnames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import styles from './Theoretical.module.scss';
import { NettingEngineSurvey, NettingEngineQuestionType, getIsChangingQuestion, setIsChangingQuestion } from '../store';
import { NettingEngineEntity } from '../../../admin/entity/store';
import { TheoreticalBooleanQuestions } from './TheoreticalBooleanQuestions';
import { TheoreticalDropdownQuestions } from './TheoreticalDropdownQuestions';
import { AgreementType } from '../../../admin/documents/store/types';

interface TheoreticalSurveyQuestionsProps {
    surveyQuestionIndex: number;
    nettingEngineSurvey: NettingEngineSurvey[];
    allEntities: NettingEngineEntity[];
    setQuestionIndex: (index: number) => void;
    counterpartyNameKnown: boolean | null;
    jurisdictionOptions: { value: string, label: string }[];
    updateCounterpartyOfficeOptions: (value: DropdownOption[]) => void;
    agreementType: AgreementType;
}

export const TheoreticalSurveyQuestions: React.FC<TheoreticalSurveyQuestionsProps> = ({
    surveyQuestionIndex,
    nettingEngineSurvey,
    allEntities,
    setQuestionIndex,
    counterpartyNameKnown,
    jurisdictionOptions,
    updateCounterpartyOfficeOptions,
    agreementType
}) => {

    const changingQuestion = useAppSelector(getIsChangingQuestion);
    const dispatch = useAppDispatch();

    const transitionToQuestionByIncrement = useCallback((increment = 1) => {
        setTimeout(() => {
            setQuestionIndex(surveyQuestionIndex + increment);
            dispatch(setIsChangingQuestion(false));
        }, 750);
    }, [dispatch, setQuestionIndex, surveyQuestionIndex]);

    const transitionToNextQuestion = useCallback(() => transitionToQuestionByIncrement(), [transitionToQuestionByIncrement]);

    const { question, type } = nettingEngineSurvey[surveyQuestionIndex];

    return (
        <div className={styles.surveyQuestionsWrapper}>
            <div className={classnames(styles.surveyQuestion, { [styles.surveyQuestionFadeIn]: !changingQuestion, [styles.surveyQuestionFadeOut]: changingQuestion })}>{question}</div>
            {type === NettingEngineQuestionType.BOOLEAN ?
                <TheoreticalBooleanQuestions
                    surveyQuestionIndex={surveyQuestionIndex}
                    nettingEngineSurvey={nettingEngineSurvey}
                    transitionToQuestionByIncrement={transitionToQuestionByIncrement}
                    transitionToNextQuestion={transitionToNextQuestion}
                />
                :
                <TheoreticalDropdownQuestions
                    surveyQuestionIndex={surveyQuestionIndex}
                    nettingEngineSurvey={nettingEngineSurvey}
                    allEntities={allEntities}
                    counterpartyNameKnown={counterpartyNameKnown}
                    jurisdictionOptions={jurisdictionOptions}
                    updateCounterpartyOfficeOptions={updateCounterpartyOfficeOptions}
                    transitionToQuestionByIncrement={transitionToQuestionByIncrement}
                    transitionToNextQuestion={transitionToNextQuestion}
                    agreementType={agreementType}
                />
            }
        </div>
    );
};
